import { useEffect, useRef } from "react";
import { usePlayer } from "@/context/PlayerContext";

// Disco laser beams following the mouse; with "Luci" on they pulse to the bass. Drawn at half resolution, ~30fps, no shadowBlur, to keep the player smooth.
const SCALE = 0.4;
const FRAME_MS = 33;

export const LaserCursor = () => {
  const ref = useRef();
  const { lights, playing, getLevel, lightStyle } = usePlayer();
  const live = useRef({});
  live.current = { lights, playing, getLevel, lightStyle };
  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    const c = ref.current, ctx = c.getContext("2d", { alpha: true });
    let w = 0, h = 0, raf = 0, hue = 150, last = 0, lastBeat = 0, lastFrame = 0, dirty = true, flash = 0, lastFlash = 0;
    const mouse = { x: -1000, y: -1000, active: false };
    const trail = [];
    const resize = () => { w = window.innerWidth; h = window.innerHeight; c.width = Math.round(w * SCALE); c.height = Math.round(h * SCALE); ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0); };
    const move = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; mouse.active = true; last = performance.now(); trail.push({ x: e.clientX, y: e.clientY, hue, life: 1 }); if (trail.length > 24) trail.shift(); };
    const leave = () => { mouse.active = false; };
    const beam = (sx, sy, hh, width) => {
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(mouse.x, mouse.y);
      ctx.strokeStyle = `hsla(${hh},100%,62%,0.22)`; ctx.lineWidth = width * 3; ctx.stroke();
      ctx.strokeStyle = `hsla(${hh},100%,75%,0.85)`; ctx.lineWidth = width; ctx.stroke();
    };
    const strobe = (t, level) => {
      // Max ~3 flashes/second (photosensitivity safe limit), soft alpha.
      if (level > 0.6 && t - lastFlash > 340) { flash = 1; lastFlash = t; }
      if (flash > 0) { ctx.fillStyle = `rgba(255,255,255,${flash * 0.32})`; ctx.fillRect(0, 0, w, h); flash = Math.max(0, flash - 0.3); }
      const x = (Math.sin(t / 400) * 0.5 + 0.5) * w;
      ctx.strokeStyle = `rgba(255,255,255,${0.25 + level * 0.5})`; ctx.lineWidth = 2 + level * 4;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(w - x, h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(w - x, 0); ctx.lineTo(x, h); ctx.stroke();
    };
    const club = (t, level) => {
      for (let i = 0; i < 4; i++) {
        const x = w * (0.2 + 0.6 * (Math.sin(t / (1800 + i * 500) + i * 1.7) * 0.5 + 0.5));
        const y = h * (0.25 + 0.5 * (Math.cos(t / (2200 + i * 400) + i) * 0.5 + 0.5));
        const hh = (hue + i * 90) % 360, rad = Math.min(w, h) * (0.18 + level * 0.12);
        [[1, 0.06], [0.65, 0.08], [0.35, 0.12]].forEach(([k, a]) => {
          ctx.fillStyle = `hsla(${hh},90%,60%,${a + level * 0.05})`; ctx.beginPath(); ctx.arc(x, y, rad * k, 0, Math.PI * 2); ctx.fill();
        });
      }
      ctx.fillStyle = `hsla(${(hue + 180) % 360},90%,55%,${0.05 + level * 0.08})`; ctx.fillRect(0, h * 0.62, w, h * 0.2);
    };
    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      if (t - lastFrame < FRAME_MS || document.hidden) return;
      lastFrame = t;
      const { lights: on, playing: pl, getLevel: lv, lightStyle: st } = live.current;
      const party = on && pl;
      const idle = t - last > 2500 || !fine;
      if (!party && idle && !trail.length) {
        if (dirty) { ctx.clearRect(0, 0, w, h); dirty = false; }
        return;
      }
      dirty = true;
      ctx.clearRect(0, 0, w, h);
      const level = party ? lv() : 0;
      if (party && level > 0.72 && t - lastBeat > 200) { hue = (hue + 70) % 360; lastBeat = t; }
      hue = (hue + (party && st === "club" ? 0.5 : 2)) % 360;
      if (party && st === "strobo") strobe(t, level);
      if (party && st === "club") club(t, level);
      const autoBeams = party && st !== "strobo" && st !== "club";
      const target = mouse.active && !idle ? mouse : autoBeams ? { x: w / 2 + Math.sin(t / 900) * w * 0.35, y: h / 2 + Math.cos(t / 700) * h * 0.3 } : null;
      if (target) {
        mouse.x = target.x; mouse.y = target.y;
        [[0, 0], [w, 0], [w / 2, 0], [0, h], [w, h]].forEach(([sx, sy], i) => beam(sx, sy, (hue + i * 72) % 360, 1.6 + level * 5));
        const rad = 40 + level * 60;
        [[1, 0.18], [0.6, 0.3], [0.3, 0.6]].forEach(([k, a]) => {
          ctx.fillStyle = `hsla(${hue},100%,70%,${a})`; ctx.beginPath(); ctx.arc(mouse.x, mouse.y, rad * k, 0, Math.PI * 2); ctx.fill();
        });
      }
      for (let i = trail.length - 1; i >= 0; i--) {
        const p = trail[i];
        p.life -= 0.07;
        if (p.life <= 0) { trail.splice(i, 1); continue; }
        ctx.fillStyle = `hsla(${(p.hue + i * 6) % 360},100%,65%,${p.life * 0.8})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, 3 * p.life + 0.5, 0, Math.PI * 2); ctx.fill();
      }
    };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseleave", leave);
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", move); document.removeEventListener("mouseleave", leave); };
  }, []);
  return <canvas ref={ref} aria-hidden="true" data-testid="laser-cursor-canvas" className="fixed inset-0 z-30 pointer-events-none w-screen h-screen" />;
};
